import { useCallback, useRef, useState } from 'react';

import appSettings from '../config/settings';
import { verifyRecaptcha } from '../util/api';
import { getRecaptchaToken } from '../util/recaptcha';
import * as log from '../util/log';

/**
 * useRecaptchaGate
 *
 * Runs an invisible reCAPTCHA v3 check just before an action (submitting a form,
 * clicking a social login button) and decides whether the action may proceed.
 *
 * The important distinction this hook draws is between a check that *said no* and
 * a check that *couldn't run*. reCAPTCHA v3 depends on a Google script loading in
 * the visitor's browser, on the site key being registered for the domain the site
 * is served from, and on our own verification endpoint being configured. Any of
 * those can be wrong for reasons that have nothing to do with the person trying to
 * log in - an ad-blocker, a corporate proxy, a key that was never registered for
 * dev.ethy.co.uk. Treating those as "you're a bot" locks out real customers with no
 * way back, so they are treated as the check being unavailable and the action goes
 * ahead. reCAPTCHA is one signal among several here, not the front door lock.
 *
 * A genuine "no" from Google does block the action, but not permanently: v3 scores
 * move around, and a single low score is not proof of anything. After
 * MAX_REJECTED_ATTEMPTS consecutive rejections the gate closes for the rest of the
 * page, which is where an actual script starts to cost something.
 */

// Statuses the gate can be in. 'skipped' means no site key is configured at all
// (local dev), so nothing is gated.
export const RECAPTCHA_SKIPPED = 'skipped';
export const RECAPTCHA_IDLE = 'idle';
export const RECAPTCHA_PENDING = 'pending';
export const RECAPTCHA_UNAVAILABLE = 'unavailable';
export const RECAPTCHA_REJECTED = 'rejected';
export const RECAPTCHA_BLOCKED = 'blocked';

export const MAX_REJECTED_ATTEMPTS = 3;

/**
 * @param {string} action - reCAPTCHA action name, e.g. 'auth' or 'inquiry'
 * @returns {Object} { status, isBlocked, isPending, errorMessageId, guard, reset }
 *   - guard(fn) wraps a callback so the check runs before it
 *   - isBlocked is true while a check is in flight or after the gate has closed
 *   - errorMessageId is a translation key to show, or null
 */
const useRecaptchaGate = action => {
  const [status, setStatus] = useState(
    appSettings.recaptchaSiteKey ? RECAPTCHA_IDLE : RECAPTCHA_SKIPPED
  );
  // A ref, not state: the count is read inside the same tick it is written.
  const rejectedAttempts = useRef(0);

  const reset = useCallback(() => {
    rejectedAttempts.current = 0;
    setStatus(appSettings.recaptchaSiteKey ? RECAPTCHA_IDLE : RECAPTCHA_SKIPPED);
  }, []);

  const guard = useCallback(
    onVerified => (...args) => {
      if (status === RECAPTCHA_SKIPPED) {
        return onVerified(...args);
      }
      // Already closed, or a check is mid-flight: ignore. The submit button is
      // disabled in both cases, so this only catches programmatic callers.
      if (status === RECAPTCHA_PENDING || status === RECAPTCHA_BLOCKED) {
        return undefined;
      }

      setStatus(RECAPTCHA_PENDING);

      return getRecaptchaToken(action)
        .then(token =>
          // No token means no site key, i.e. nothing to verify against.
          token == null ? { unavailable: true } : verifyRecaptcha(token, action)
        )
        .then(result => {
          if (result.unavailable) {
            setStatus(RECAPTCHA_UNAVAILABLE);
            return onVerified(...args);
          }
          if (result.success) {
            rejectedAttempts.current = 0;
            setStatus(RECAPTCHA_IDLE);
            return onVerified(...args);
          }

          rejectedAttempts.current += 1;
          setStatus(
            rejectedAttempts.current >= MAX_REJECTED_ATTEMPTS
              ? RECAPTCHA_BLOCKED
              : RECAPTCHA_REJECTED
          );
          return undefined;
        })
        .catch(e => {
          // Anything thrown here - the script never loaded, the verification
          // endpoint is unconfigured or down - means we never got an answer.
          // Log it so it's visible in Sentry, then let the person through.
          log.error(e, 'recaptcha-unavailable', { recaptchaAction: action });
          setStatus(RECAPTCHA_UNAVAILABLE);
          return onVerified(...args);
        });
    },
    [action, status]
  );

  const errorMessageId =
    status === RECAPTCHA_BLOCKED
      ? 'Recaptcha.blocked'
      : status === RECAPTCHA_REJECTED
      ? 'Recaptcha.rejected'
      : null;

  return {
    status,
    isPending: status === RECAPTCHA_PENDING,
    isBlocked: status === RECAPTCHA_PENDING || status === RECAPTCHA_BLOCKED,
    isEnabled: status !== RECAPTCHA_SKIPPED,
    errorMessageId,
    guard,
    reset,
  };
};

export default useRecaptchaGate;
